require('./bootstrap');

import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
// Deep imports (not `from 'lucide'`): the package entry re-exports every
// icon, and Mix dev builds don't tree-shake, which made app.js ~7MB.
import replaceElement from 'lucide/dist/esm/replaceElement.mjs';
import icons from './icons';

Alpine.plugin(collapse);
Alpine.plugin(focus);

/**
 * Renders every `data-lucide` element on the page as an inline SVG icon.
 * Exposed as window.refreshIcons so Alpine components / anything that
 * injects markup at runtime (e.g. an AJAX-rendered list, an Alpine
 * x-if/x-for block) can re-render icons it just added, since lucide only
 * scans the DOM once when called.
 *
 * Only the icons listed in ./icons.js are bundled — add new names there.
 *
 * Defined and assigned to window BEFORE Alpine.start(): an x-init="refreshIcons()"
 * in the very first component Alpine initializes runs synchronously inside
 * that start() call, so the global must already exist by then.
 */
function renderIcons(root) {
    // Same as lucide's createIcons(), minus the full-set import. Skips
    // already-rendered <svg data-lucide> so repeat calls are cheap.
    (root || document).querySelectorAll('[data-lucide]:not(svg)').forEach(function (el) {
        replaceElement(el, { nameAttr: 'data-lucide', icons: icons, attrs: {} });
    });
}

window.refreshIcons = renderIcons;

/* ------------------------------------------------------------------ */
/* Local-time rendering for <span data-utc="unix" data-format="short|full|relative"> */
/* ------------------------------------------------------------------ */
function relative(date) {
    const diff = (Date.now() - date.getTime()) / 1000;
    const abs = Math.abs(diff);
    const rtf = typeof Intl !== 'undefined' && Intl.RelativeTimeFormat ? new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }) : null;
    if (!rtf) return date.toLocaleString();
    const units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
    for (let i = 0; i < units.length; i++) {
        if (abs >= units[i][1]) return rtf.format(-Math.round(diff / units[i][1]), units[i][0]);
    }
    return 'just now';
}

function convertToLocalTime(root) {
    (root || document).querySelectorAll('[data-utc]').forEach(function (el) {
        const utc = el.getAttribute('data-utc');
        if (!utc) return;
        const date = new Date(utc * 1000);
        const format = el.getAttribute('data-format') || 'full';
        if (format === 'relative') {
            el.textContent = relative(date);
            el.setAttribute('title', date.toLocaleString());
            return;
        }
        const options = format === 'short'
            ? { year: 'numeric', month: 'short', day: 'numeric' }
            : { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' };
        el.textContent = date.toLocaleString(undefined, options);
    });
}
window.renderLocalTimes = convertToLocalTime;

/* ------------------------------------------------------------------ */
/* Storage helpers — never throw (private mode / blocked storage).     */
/* ------------------------------------------------------------------ */
const store = {
    get(key) { try { return localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch (e) { /* ignore */ } },
};

/* ------------------------------------------------------------------ */
/* App shell: theme, sidebar (collapsed / mobile drawer).              */
/* ------------------------------------------------------------------ */
Alpine.data('shell', () => ({
    dark: document.documentElement.classList.contains('dark'),
    collapsed: document.documentElement.dataset.sidebar === 'collapsed',
    drawer: false,
    toggleTheme() {
        this.dark = !this.dark;
        document.documentElement.classList.toggle('dark', this.dark);
        store.set('hal.theme', this.dark ? 'dark' : 'light');
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', this.dark ? '#1A1A1A' : '#F6F6F6');
        const icon = document.querySelector('[data-platform-icon]');
        if (icon) icon.href = this.dark ? icon.dataset.darkIcon : icon.dataset.lightIcon;
    },
    toggleSidebar() {
        this.collapsed = !this.collapsed;
        if (this.collapsed) document.documentElement.dataset.sidebar = 'collapsed';
        else delete document.documentElement.dataset.sidebar;
        store.set('hal.sidebar', this.collapsed ? 'collapsed' : 'expanded');
    },
}));

/* ------------------------------------------------------------------ */
/* ⌘K command palette. Data comes from <script id="palette-data">.     */
/* ------------------------------------------------------------------ */
Alpine.data('palette', () => ({
    open: false,
    query: '',
    active: 0,
    items: [],
    init() {
        const el = document.getElementById('palette-data');
        try { this.items = el ? JSON.parse(el.textContent) : []; } catch (e) { this.items = []; }
        window.addEventListener('keydown', (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                this.open ? this.close() : this.show();
            } else if (e.key === '/' && !this.open && !/INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName) && !document.activeElement.isContentEditable) {
                e.preventDefault();
                this.show();
            }
        });
        window.addEventListener('open-palette', () => this.show());
    },
    show() {
        this.open = true;
        this.query = '';
        this.active = 0;
        this.$nextTick(() => { this.$refs.input && this.$refs.input.focus(); window.refreshIcons(); });
    },
    close() { this.open = false; },
    get results() {
        const q = this.query.trim().toLowerCase();
        const list = !q ? this.items : this.items
            .map((item) => {
                const hay = (item.label + ' ' + (item.group || '') + ' ' + (item.keywords || '')).toLowerCase();
                let score = -1;
                if (item.label.toLowerCase().startsWith(q)) score = 3;
                else if (item.label.toLowerCase().includes(q)) score = 2;
                else if (hay.includes(q)) score = 1;
                else {
                    // Loose subsequence match ("envs" → "Environments").
                    let i = 0;
                    for (const ch of hay) { if (ch === q[i]) i++; if (i === q.length) break; }
                    if (i === q.length) score = 0;
                }
                return { item, score };
            })
            .filter((r) => r.score >= 0)
            .sort((a, b) => b.score - a.score)
            .map((r) => r.item);
        return list.slice(0, 40);
    },
    get grouped() {
        const groups = [];
        const index = {};
        this.results.forEach((item, i) => {
            const g = item.group || 'Go to';
            if (!(g in index)) { index[g] = groups.length; groups.push({ name: g, items: [] }); }
            // _k is stable per item so Alpine never reuses a row (and its
            // already-rendered SVG icon) for a different item.
            groups[index[g]].items.push(Object.assign({ _i: i, _k: g + '|' + item.label + '|' + (item.url || item.action || '') }, item));
        });
        return groups;
    },
    move(delta) {
        const n = this.results.length;
        if (!n) return;
        this.active = (this.active + delta + n) % n;
        this.$nextTick(() => {
            const el = this.$root.querySelector('[data-palette-index="' + this.active + '"]');
            if (el) el.scrollIntoView({ block: 'nearest' });
        });
    },
    run(item) {
        item = item || this.results[this.active];
        if (!item) return;
        this.close();
        if (item.action === 'theme') { window.dispatchEvent(new CustomEvent('toggle-theme')); return; }
        if (item.url) window.location.href = item.url;
    },
    onQuery() { this.active = 0; this.$nextTick(() => window.refreshIcons()); },
}));

/* ------------------------------------------------------------------ */
/* Confirm dialog — replaces onsubmit="return confirm(...)".           */
/* Any <form data-confirm="Question?"> is intercepted on submit.       */
/* Optional: data-confirm-tone="danger|warning|primary",               */
/*           data-confirm-action="Delete" (button label).              */
/* ------------------------------------------------------------------ */
Alpine.data('confirmDialog', () => ({
    open: false,
    message: '',
    tone: 'primary',
    action: 'Continue',
    form: null,
    init() {
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-confirm')) return;
            if (form.dataset.confirmed === '1') return;
            e.preventDefault();
            this.form = form;
            this.message = form.getAttribute('data-confirm');
            this.tone = form.getAttribute('data-confirm-tone') || 'primary';
            this.action = form.getAttribute('data-confirm-action') || 'Continue';
            this.open = true;
            this.$nextTick(() => { this.$refs.confirm && this.$refs.confirm.focus(); window.refreshIcons(); });
        }, true);
        // Non-form confirmations (e.g. a JS-driven fetch flow) go through
        // window.confirmAction(message, opts) → Promise<boolean> instead of
        // the blocking confirm(). See the dispatch below.
        window.addEventListener('open-confirm', (e) => {
            this.form = null;
            this.message = e.detail.message;
            this.tone = e.detail.tone || 'primary';
            this.action = e.detail.action || 'Continue';
            this._resolve = e.detail.resolve;
            this.open = true;
            this.$nextTick(() => { this.$refs.confirm && this.$refs.confirm.focus(); window.refreshIcons(); });
        });
    },
    accept() {
        if (this.form) {
            const form = this.form;
            this.open = false;
            form.dataset.confirmed = '1';
            form.querySelectorAll('button[type="submit"], button:not([type])').forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
            form.submit();
            return;
        }
        this.open = false;
        if (this._resolve) { this._resolve(true); this._resolve = null; }
    },
    cancel() {
        this.open = false;
        this.form = null;
        if (this._resolve) { this._resolve(false); this._resolve = null; }
    },
}));

/**
 * Promise-based replacement for window.confirm() that uses the platform
 * confirm dialog. Usage: window.confirmAction('Sure?', { tone: 'warning',
 * action: 'Continue' }).then((ok) => { if (ok) { ... } });
 */
window.confirmAction = function (message, opts) {
    opts = opts || {};
    return new Promise((resolve) => {
        window.dispatchEvent(new CustomEvent('open-confirm', { detail: { message: message, tone: opts.tone, action: opts.action, resolve: resolve } }));
    });
};

/* ------------------------------------------------------------------ */
/* Toasts. Initial flash messages come from <script id="flash-data">;  */
/* anything can add one with window.toast('Saved', 'success').         */
/* ------------------------------------------------------------------ */
Alpine.data('toasts', () => ({
    list: [],
    seq: 0,
    init() {
        const el = document.getElementById('flash-data');
        let initial = [];
        try { initial = el ? JSON.parse(el.textContent) : []; } catch (e) { initial = []; }
        initial.forEach((t, i) => setTimeout(() => this.push(t.message, t.type), 150 + i * 120));
        window.toast = (message, type) => this.push(message, type);
    },
    push(message, type) {
        const id = ++this.seq;
        this.list.push({ id, message, type: type || 'info', shown: true });
        this.$nextTick(() => window.refreshIcons());
        // Errors stay until dismissed — they usually say what to do next.
        if (type !== 'error') setTimeout(() => this.dismiss(id), 6000);
    },
    dismiss(id) {
        const t = this.list.find((x) => x.id === id);
        if (t) t.shown = false;
        setTimeout(() => { this.list = this.list.filter((x) => x.id !== id); }, 250);
    },
}));

/* Small dropdown/menu helper (user menu, card overflow). */
Alpine.data('menu', () => ({
    open: false,
    toggle() { this.open = !this.open; if (this.open) this.$nextTick(() => window.refreshIcons()); },
    close() { this.open = false; },
}));

window.Alpine = Alpine;
Alpine.start();

function boot() {
    renderIcons();
    if (document.documentElement.classList.contains('platform')) convertToLocalTime();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
